import React, { useState } from "react";
import { Modal } from "../common/Modal";
import { useStaffDirectory } from "../../services/staffService";
import { UserCheck, UserX, Search, Loader2 } from "lucide-react";

interface BulkAssignModalProps {
  isOpen: boolean;
  onClose: () => void;
  count: number;
  onAssign: (staff: { displayName: string; email: string } | null) => Promise<void>;
}

export const BulkAssignModal: React.FC<BulkAssignModalProps> = ({
  isOpen,
  onClose,
  count,
  onAssign,
}) => {
  const { staff, loading } = useStaffDirectory({ rfpOnly: true });
  const [search, setSearch] = useState("");
  const [selectedEmail, setSelectedEmail] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const filteredStaff = staff.filter((s) => {
    const q = search.toLowerCase();
    return s.displayName.toLowerCase().includes(q) || s.email.toLowerCase().includes(q);
  });

  const handleConfirm = async () => {
    setIsSubmitting(true);
    try {
      if (selectedEmail === "__unassign__") {
        await onAssign(null);
      } else if (selectedEmail) {
        const found = staff.find((s) => s.email === selectedEmail);
        if (found) {
          await onAssign({ displayName: found.displayName, email: found.email });
        }
      }
      onClose();
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={`Assign ${count} Requirement${count === 1 ? "" : "s"}`}
    >
      <div className="space-y-4">
        <p className="text-xs text-slate-500">
          Choose a team member to assign to the {count} selected requirement
          {count === 1 ? "" : "s"}.
        </p>

        {/* Search Input */}
        <div className="relative">
          <Search className="absolute left-2.5 top-2.5 h-3.5 w-3.5 text-slate-400" />
          <input
            type="text"
            placeholder="Search team by name or email..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full rounded-lg border border-slate-200 bg-white py-1.5 pl-8 pr-3 text-xs text-slate-900 outline-hidden focus:border-[#2ba5b5] dark:border-slate-700 dark:bg-slate-950 dark:text-slate-100"
          />
        </div>

        {/* Staff list */}
        <div className="max-h-60 space-y-1.5 overflow-y-auto rounded-lg border border-slate-200/80 p-2 dark:border-slate-800">
          {/* Unassign option */}
          <label
            className={`flex cursor-pointer items-center justify-between rounded-md p-2 text-xs transition-colors ${
              selectedEmail === "__unassign__"
                ? "bg-rose-50 text-rose-900 dark:bg-rose-950/40 dark:text-rose-300"
                : "hover:bg-slate-50 text-slate-700 dark:text-slate-300 dark:hover:bg-slate-800/60"
            }`}
          >
            <div className="flex items-center gap-2">
              <UserX className="h-4 w-4 text-slate-400" />
              <div>
                <div className="font-semibold">Unassign (Remove Assignee)</div>
                <div className="text-[11px] text-slate-400">Clear current owner from selected</div>
              </div>
            </div>
            <input
              type="radio"
              name="selected-staff"
              checked={selectedEmail === "__unassign__"}
              onChange={() => setSelectedEmail("__unassign__")}
              className="text-[#2ba5b5] focus:ring-[#2ba5b5]"
            />
          </label>

          {loading ? (
            <div className="flex items-center justify-center p-4 text-xs text-slate-400">
              <Loader2 className="mr-2 h-4 w-4 animate-spin text-[#2ba5b5]" />
              Loading team directory...
            </div>
          ) : filteredStaff.length === 0 ? (
            <div className="p-3 text-center text-xs text-slate-400">No matching staff found</div>
          ) : (
            filteredStaff.map((person) => (
              <label
                key={person.email}
                className={`flex cursor-pointer items-center justify-between rounded-md p-2 text-xs transition-colors ${
                  selectedEmail === person.email
                    ? "bg-[#2ba5b5]/10 text-slate-900 dark:bg-[#2ba5b5]/20 dark:text-slate-100"
                    : "hover:bg-slate-50 text-slate-700 dark:text-slate-300 dark:hover:bg-slate-800/60"
                }`}
              >
                <div className="flex items-center gap-2">
                  <div className="flex h-7 w-7 items-center justify-center rounded-full bg-[#2ba5b5]/20 text-[11px] font-bold text-[#1b7582] dark:text-[#52c8d7]">
                    {person.displayName.slice(0, 2).toUpperCase()}
                  </div>
                  <div>
                    <div className="font-semibold text-slate-900 dark:text-slate-100">
                      {person.displayName}
                    </div>
                    <div className="text-[11px] text-slate-400">
                      {person.email}
                      {person.department ? ` · ${person.department}` : ""}
                    </div>
                  </div>
                </div>
                <input
                  type="radio"
                  name="selected-staff"
                  checked={selectedEmail === person.email}
                  onChange={() => setSelectedEmail(person.email)}
                  className="text-[#2ba5b5] focus:ring-[#2ba5b5]"
                />
              </label>
            ))
          )}
        </div>

        {/* Modal actions */}
        <div className="flex items-center justify-end gap-2 pt-2 border-t border-slate-200 dark:border-slate-800">
          <button
            type="button"
            onClick={onClose}
            className="rounded-lg border border-slate-200 px-3.5 py-1.5 text-xs font-medium text-slate-700 hover:bg-slate-50 dark:border-slate-700 dark:text-slate-300 dark:hover:bg-slate-800"
          >
            Cancel
          </button>
          <button
            type="button"
            disabled={!selectedEmail || isSubmitting}
            onClick={handleConfirm}
            className="btn-animate inline-flex items-center gap-1.5 rounded-lg bg-[#2ba5b5] px-3.5 py-1.5 text-xs font-semibold text-white shadow-xs hover:bg-[#238997] disabled:opacity-50"
          >
            {isSubmitting ? (
              <Loader2 className="h-3.5 w-3.5 animate-spin" />
            ) : (
              <UserCheck className="h-3.5 w-3.5" />
            )}
            <span>Apply Assignment</span>
          </button>
        </div>
      </div>
    </Modal>
  );
};
